import debounce from "../../utils/debounce";
import constants from "../../constants";

const getBannerVideoProvider = (videoIframe) => {
    const provider = (videoIframe.getAttribute('data-video-provider') || '').trim().toLowerCase();
    if (provider) {
        return provider;
    }

    const src = (videoIframe.getAttribute('src') || '').toLowerCase();
    if (src.includes('youtube.com') || src.includes('youtu.be')) {
        return 'youtube';
    }

    if (src.includes('vimeo.com')) {
        return 'vimeo';
    }

    return '';
};

const getIframeOrigin = (videoIframe) => {
    const src = videoIframe ? (videoIframe.getAttribute('src') || '') : '';
    if (!src) {
        return '*';
    }

    try {
        return new URL(src, window.location.href).origin;
    } catch (error) {
        return '*';
    }
};

const postYoutubeCommand = (videoIframe, payload) => {
    if (!videoIframe || !videoIframe.contentWindow) {
        return;
    }

    videoIframe.contentWindow.postMessage(JSON.stringify(payload), getIframeOrigin(videoIframe));
};

const postVimeoCommand = (videoIframe, payload) => {
    if (!videoIframe || !videoIframe.contentWindow) {
        return;
    }

    videoIframe.contentWindow.postMessage(payload, getIframeOrigin(videoIframe));
};

const setBannerVideoAudioState = (videoIframe, videoProvider, isAudible) => {
    if (videoProvider === 'youtube') {
        const youtubeMuteCommand = {
            event: 'command',
            func: isAudible ? 'unMute' : 'mute',
            args: []
        };
        const youtubeVolumeCommand = {
            event: 'command',
            func: 'setVolume',
            args: [isAudible ? 100 : 0]
        };

        // Retry briefly in case the embedded player API is still initializing.
        [0, 120, 280, 520].forEach((delay) => {
            window.setTimeout(() => {
                postYoutubeCommand(videoIframe, youtubeMuteCommand);
                postYoutubeCommand(videoIframe, youtubeVolumeCommand);
            }, delay);
        });
        return;
    }

    if (videoProvider === 'vimeo') {
        const vimeoMutedCommand = {
            method: 'setMuted',
            value: !isAudible
        };
        const vimeoVolumeCommand = {
            method: 'setVolume',
            value: isAudible ? 1 : 0
        };

        postVimeoCommand(videoIframe, vimeoMutedCommand);
        postVimeoCommand(videoIframe, vimeoVolumeCommand);
    }
};

const setUpHomeHeroBannerAudioToggle = () => {
    const audioToggles = document.querySelectorAll('[data-video-audio-toggle]');
    if (!audioToggles.length) {
        return;
    }

    audioToggles.forEach((toggleButton) => {
        const bannerElement = toggleButton.closest('.home-hero-banner');
        if (!bannerElement) {
            return;
        }

        const videoIframe = bannerElement.querySelector('.home-hero-banner__video');
        if (!videoIframe) {
            return;
        }

        const videoProvider = getBannerVideoProvider(videoIframe);
        if (!videoProvider) {
            return;
        }

        let isAudible = false;

        const updateToggleUi = () => {
            const nextLabel = isAudible ? 'Turn banner sound off' : 'Turn banner sound on';
            toggleButton.setAttribute('aria-pressed', isAudible ? 'true' : 'false');
            toggleButton.setAttribute('aria-label', nextLabel);
        };

        const applyAudioState = () => {
            setBannerVideoAudioState(videoIframe, videoProvider, isAudible);
            updateToggleUi();
        };

        updateToggleUi();
        toggleButton.addEventListener('click', function () {
            isAudible = !isAudible;
            applyAudioState();
        });
    });
};

const handleImageBanners = () => {
    if (document.querySelector('.image-banner')) {
        // Image banner PHP setup loads mobile image eagerly and desktop image lazily by default.
        // If viewport is not mobile-sized, load desktop image eagerly and try to de-prioritize mobile image loading.
        if (window.innerWidth >= constants.screenWidthBreakpoints.sm) {
            let mobileBannerImages = document.getElementsByClassName('image-banner-image single-image-mobile') || [];
            for (let i = 0; i < mobileBannerImages.length; i++) {
                let mobileBannerImage = mobileBannerImages[i];
                mobileBannerImage.setAttribute('decoding', "async");
                mobileBannerImage.setAttribute('loading', "lazy");
                mobileBannerImage.setAttribute('fetchpriority', "low");
            }

            let desktopBannerImages = document.getElementsByClassName('image-banner-image single-image-desktop') || [];
            for (let k = 0; k < desktopBannerImages.length; k++) {
                let desktopBannerImage = desktopBannerImages[k];
                desktopBannerImage.setAttribute('decoding', "auto");
                desktopBannerImage.setAttribute('loading', "eager");
                desktopBannerImage.setAttribute('fetchpriority', "high");
            }
        }

        // handle video in image banner if present
        const imageBannerVideoElement = document.querySelector('#imageBannerVideo');
        if (imageBannerVideoElement) {
            const imageBannerVideoLoader = function () {
                const videoUrl = imageBannerVideoElement.getAttribute('data-video-url');
                let alreadyHasSource = imageBannerVideoElement.querySelector('source');
                if (window.innerWidth >= constants.screenWidthBreakpoints.sm && !alreadyHasSource) {
                    imageBannerVideoElement.innerHTML = `<source src="${videoUrl}">`;
                    const imageBannerVideoSource = imageBannerVideoElement.querySelector('source');
                    if (imageBannerVideoSource) {
                        // show video tag background fallback image after 1 second (to catch case where video fails to load).
                        let fallbackImageUrl =
                            document.querySelector('.image-banner-image.single-image-desktop').getAttribute('src');
                        window.setTimeout(function () {
                            imageBannerVideoElement.setAttribute('style', `background-image: url(${fallbackImageUrl})`)
                        }, 1000);
                    }
                }
            };

            const setupImageBannerVideoLoader = function () {
                // execute once on load
                imageBannerVideoLoader();

                // execute on resize, debounced
                window.addEventListener("resize", debounce(function () {
                    imageBannerVideoLoader();
                }, 250));
            }

            // execute setup
            setupImageBannerVideoLoader();
        }

        setUpHomeHeroBannerAudioToggle();
    }
}

export default handleImageBanners;
